import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';
import { useJobStream } from '@/api/jobs';
import type { Website } from '@/api/types';
import { useSession } from '@/app/session';
import { Icon, Pill, useToast, type IconName, type Tone } from '@/design-system';
import { now } from '@/lib/clock';
import { daysUntil, shortName } from '@/lib/format';
import { LoadError } from '@/shell/LoadError';
import { activeDeployment, deploymentsQuery, healthQuery, revisionLabel, runningDeployment, tileStates, type DeploymentOut, type TileState } from './api';
import { percent, seconds, whenLabel } from './format';
import { RollbackModal } from './RollbackModal';
import s from './deploy.module.css';

const TILES: { key: 'generate' | 'validate' | 'upload' | 'activate'; icon: IconName }[] = [
  { key: 'generate', icon: 'file-code-2' },
  { key: 'validate', icon: 'shield-check' },
  { key: 'upload', icon: 'cloud-upload' },
  { key: 'activate', icon: 'zap' },
];

const STATE_ICON: Record<TileState, { icon: IconName; className: string }> = {
  done: { icon: 'circle-check', className: s.done ?? '' },
  current: { icon: 'loader', className: s.current ?? '' },
  pending: { icon: 'circle-dashed', className: s.pendingIcon ?? '' },
  failed: { icon: 'x', className: s.failedIcon ?? '' },
};

type RowStatus = 'active' | 'failed' | 'archive' | 'activating';
const ROW_STATUS: Record<DeploymentOut['status'], RowStatus> = { active: 'active', failed: 'failed', superseded: 'archive', rolled_back: 'archive', activating: 'activating' };
const ROW_TONE: Record<RowStatus, Tone> = { active: 'acc', failed: 'err', archive: 'mute', activating: 'acc' };

/**
 * /sites/:id/deploy — «Деплой» (DESIGN_SPEC §4.5): the four pipeline tiles (live from the running publish
 * job's step), «История публикаций» with «Откатить» on archived rows (the rollback modal), and Health.
 */
export function DeployTab() {
  const site = useOutletContext<Website>();
  const { t } = useTranslation('deploy');
  const { t: tc } = useTranslation('common');
  const q = useQuery(deploymentsQuery(site.id));
  const health = useQuery(healthQuery(site.id));
  const [target, setTarget] = useState<DeploymentOut | null>(null);
  const { can } = useSession();
  const toast = useToast();
  const list = q.data;
  const running = runningDeployment(list);
  const active = activeDeployment(list);
  const stream = useJobStream(running?.job_id);
  const monthsShort = tc('months_short_genitive', { returnObjects: true }) as unknown as string[];
  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  if (!list) return null;
  const dash = tc('dash');
  const states = tileStates({ running: !!running, step: stream.job?.step ?? null, failed: stream.job?.status === 'failed', anyActive: !!active });
  const shown = running ?? active ?? list[0] ?? null;
  const rev = revisionLabel(shown?.revision ?? null) ?? site.variant ?? dash;
  const h = health.data;
  const tlsDays = h?.tls_expires_at ? daysUntil(h.tls_expires_at, now()) : null;
  return (
    <>
      <ol className={s.pipe} aria-label={t('pipe.label')}>
        {TILES.map((tile, i) => {
          const state = states[i] ?? 'pending';
          const mark = STATE_ICON[state];
          return (
            <li key={tile.key} className={s.tile} aria-current={state === 'current' ? 'step' : undefined}>
              <div className={s.tileTop}>
                <span className={s.tileNo}>{String(i + 1).padStart(2, '0')}</span>
                <Icon name={mark.icon} size={15} className={mark.className} title={t(`pipe.state.${state}`)} />
              </div>
              <div className={s.tileIcon}>
                <Icon name={tile.icon} size={18} />
              </div>
              <div className={s.tileText}>
                <span className={s.tileName}>{t(`pipe.${tile.key}`)}</span>
                <span className={s.tileDesc}>{tile.key === 'generate' ? t('pipe.generate_text', { rev }) : t(`pipe.${tile.key}_text`)}</span>
              </div>
            </li>
          );
        })}
      </ol>
      <div className={s.cols}>
        <section className={s.history} aria-labelledby="deploy-history">
          <h2 id="deploy-history" className={s.historyTitle}>
            {t('history.title')}
          </h2>
          {list.length === 0 ? (
            <div className={s.empty}>{t('history.empty')}</div>
          ) : (
            <ul className={s.rows}>
              {list.map((d) => {
                const st = ROW_STATUS[d.status];
                const canRollback = d.can_rollback && !d.is_active && !running;
                return (
                  <li key={d.id} className={s.row}>
                    <span className={s.version}>{t('history.version', { version: d.version })}</span>
                    <span className={s.rev}>{revisionLabel(d.revision) ?? dash}</span>
                    <span className={s.muted}>{t(`history.channel.${d.channel}`)}</span>
                    <span className={s.muted}>{d.actor ? shortName(d.actor.name) : dash}</span>
                    <span className={s.when}>{whenLabel(d.activated_at ?? d.created_at, t, monthsShort)}</span>
                    <Pill tone={ROW_TONE[st]} size="sm" start>
                      {t(`history.status.${st}`)}
                    </Pill>
                    {canRollback ? (
                      <button
                        type="button"
                        className={s.rollback}
                        aria-label={t('history.rollback_label', { version: d.version })}
                        aria-disabled={(d.channel === 'production' && !can('deployment:publish_production')) || undefined}
                        title={d.channel === 'production' && !can('deployment:publish_production') ? t('history.rollback_forbidden') : undefined}
                        onClick={() => (d.channel === 'production' && !can('deployment:publish_production') ? toast(t('history.rollback_forbidden')) : setTarget(d))}
                      >
                        <Icon name="undo-2" size={13} />
                        {t('history.rollback')}
                      </button>
                    ) : (
                      <span />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        <section className={s.health} aria-labelledby="deploy-health">
          <span id="deploy-health" className={s.healthTitle}>
            {t('health.title')}
          </span>
          <dl className={s.healthList}>
            <div className={s.healthRow}>
              <dt className={s.healthKey}>{t('health.uptime')}</dt>
              <dd className={s.healthValue}>{h?.uptime_30d != null ? percent(h.uptime_30d, 2) : dash}</dd>
            </div>
            <div className={s.healthRow}>
              <dt className={s.healthKey}>{t('health.lcp')}</dt>
              <dd className={s.healthValue}>{h?.lcp_p75_ms != null ? seconds(h.lcp_p75_ms, t) : dash}</dd>
            </div>
            <div className={s.healthRow}>
              <dt className={s.healthKey}>{t('health.tls')}</dt>
              <dd className={s.healthValue}>{tlsDays === null ? dash : tlsDays < 0 ? t('health.tls_expired') : t('health.tls_in', { count: tlsDays })}</dd>
            </div>
            <div className={s.healthRow}>
              <dt className={s.healthKey}>{t('health.cdn')}</dt>
              <dd className={s.healthValue}>{h?.cdn_cache_hit != null ? percent(h.cdn_cache_hit, 1) : dash}</dd>
            </div>
          </dl>
        </section>
      </div>
      <RollbackModal site={site} current={target ? activeDeployment(list, target.channel) : active} target={target} onClose={() => setTarget(null)} />
    </>
  );
}
